import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";

import {
  BrowserAlert,
  BrowserNotificationPreferences,
  BrowserNotificationType,
  BrowserPermission,
  DEFAULT_BROWSER_NOTIFICATIONS,
  deliverBrowserAlert,
  getBrowserPermission,
  preferencesKey,
  readBrowserPreferences,
  startForegroundHeartbeat
} from "./browser-notifications.util";

// Chat is switched on and off on the user's account (Personal Settings → Chat,
// `chat_notifications_enabled`), so the per-computer preference no longer
// gates it; the chat hook checks the account setting before calling notify.
const isLocallyEnabled = (
  preferences: BrowserNotificationPreferences,
  type: BrowserNotificationType
) => type === "chat" || preferences[type];

const BrowserNotificationsContext = createContext({
  permission: "unavailable" as BrowserPermission,
  preferences: DEFAULT_BROWSER_NOTIFICATIONS,
  storageError: false,
  setPreference: (_type: BrowserNotificationType, _enabled: boolean) => {},
  requestPermission: async () => {},
  offerPermission: () => {},
  notify:
    (_alert: BrowserAlert): (() => void) =>
    () => {}
});

export const useBrowserNotifications = () =>
  useContext(BrowserNotificationsContext);

export function BrowserNotificationsProvider({
  children
}: {
  children: ReactNode;
}) {
  const user = useAppSelector(selectCurrentUser);
  const practice = useAppSelector(selectCurrentPractice);
  // Tell the other Suno tabs while this one is focused (see the dispatcher).
  useEffect(() => startForegroundHeartbeat(), []);
  // Remount on identity changes to close notifications and invalidate queued
  // deliveries/clicks before another user or practice can see them.
  return (
    <ScopedBrowserNotifications
      key={`${practice?.id}:${user?.id}`}
      scope={`${practice?.id}:${user?.id}`}
      active={Boolean(user?.id && user.id !== -1)}
    >
      {children}
    </ScopedBrowserNotifications>
  );
}

function ScopedBrowserNotifications({
  scope,
  active,
  children
}: {
  scope: string;
  active: boolean;
  children: ReactNode;
}) {
  const [preferences, setPreferences] =
    useState<BrowserNotificationPreferences>(() =>
      readBrowserPreferences(scope)
    );
  const [permission, setPermission] = useState(getBrowserPermission);
  const [storageError, setStorageError] = useState(false);
  const [prompt, setPrompt] = useState(false);
  const preferencesRef = useRef(preferences);
  const alive = useRef(true);
  const notifications = useRef(
    new Map<Notification, BrowserNotificationType>()
  );
  const location = useLocation();
  const offered = useRef(false);

  useEffect(() => {
    alive.current = true;
    const current = notifications.current;
    const refresh = () => setPermission(getBrowserPermission());
    const sync = (event: StorageEvent) => {
      if (event.key === preferencesKey(scope) || event.key === null) {
        const next = readBrowserPreferences(scope);
        preferencesRef.current = next;
        setPreferences(next);
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("storage", sync);
    return () => {
      alive.current = false;
      current.forEach((_, notification) => notification.close());
      current.clear();
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("storage", sync);
    };
  }, [scope]);

  useEffect(() => {
    notifications.current.forEach((type, notification) => {
      if (!isLocallyEnabled(preferences, type) || permission !== "granted") {
        notification.close();
        notifications.current.delete(notification);
      }
    });
  }, [preferences, permission]);

  const offerPermission = useCallback(() => {
    if (!active || offered.current || getBrowserPermission() !== "default")
      return;
    offered.current = true;
    try {
      if (localStorage.getItem(`${preferencesKey(scope)}:prompted`)) return;
    } catch {
      /* session fallback */
    }
    setPrompt(true);
  }, [active, scope]);

  useEffect(() => {
    if (
      location.pathname.startsWith("/chat") ||
      location.pathname.startsWith("/calls")
    )
      offerPermission();
  }, [location.pathname, offerPermission]);

  const dismissPrompt = useCallback(() => {
    setPrompt(false);
    try {
      localStorage.setItem(`${preferencesKey(scope)}:prompted`, "1");
    } catch {
      /* session fallback */
    }
  }, [scope]);

  const requestPermission = useCallback(async () => {
    // Called directly by a button click: browsers require user activation.
    try {
      if (getBrowserPermission() === "default")
        await Notification.requestPermission();
    } finally {
      if (alive.current) {
        setPermission(getBrowserPermission());
        dismissPrompt();
      }
    }
  }, [dismissPrompt]);

  const setPreference = useCallback(
    (type: BrowserNotificationType, enabled: boolean) => {
      const next = { ...preferencesRef.current, [type]: enabled };
      preferencesRef.current = next;
      setPreferences(next);
      try {
        localStorage.setItem(preferencesKey(scope), JSON.stringify(next));
        setStorageError(false);
      } catch {
        setStorageError(true);
      }
    },
    [scope]
  );

  const notify = useCallback(
    (alert: BrowserAlert) => {
      if (isLocallyEnabled(preferencesRef.current, alert.type))
        offerPermission();
      let cancelled = false;
      let notification: Notification | undefined;
      void deliverBrowserAlert(
        scope,
        alert,
        () =>
          active &&
          alive.current &&
          !cancelled &&
          isLocallyEnabled(preferencesRef.current, alert.type),
        (created) => {
          notification = created;
          // Bound retained click handlers in long-running sessions, even on
          // browsers that do not emit close when a banner expires.
          if (notifications.current.size >= 50) {
            const oldest = notifications.current.keys().next().value;
            oldest?.close();
            if (oldest) notifications.current.delete(oldest);
          }
          // A banner with the same tag has just been replaced on screen; drop
          // it here (without closing: closing by tag could take the new one).
          if (created.tag)
            notifications.current.forEach((_, existing) => {
              if (existing.tag === created.tag)
                notifications.current.delete(existing);
            });
          notifications.current.set(created, alert.type);
          created.onclose = () => notifications.current.delete(created);
        }
      );
      return () => {
        cancelled = true;
        notification?.close();
        if (notification) notifications.current.delete(notification);
      };
    },
    [active, scope, offerPermission]
  );

  const value = useMemo(
    () => ({
      permission,
      preferences,
      storageError,
      setPreference,
      requestPermission,
      offerPermission,
      notify
    }),
    [
      permission,
      preferences,
      storageError,
      setPreference,
      requestPermission,
      offerPermission,
      notify
    ]
  );
  return (
    <BrowserNotificationsContext.Provider value={value}>
      {children}
      <Snackbar
        open={prompt}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          onClose={dismissPrompt}
          action={
            <Button
              color="inherit"
              onClick={() => {
                void requestPermission().catch(() => {});
              }}
            >
              Enable notifications
            </Button>
          }
        >
          Get call and chat alerts while working in other apps. Keep a Suno tab
          open. You can change this in Personal Settings → Notification
          Preferences.
        </Alert>
      </Snackbar>
    </BrowserNotificationsContext.Provider>
  );
}
